import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Moon, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const links = [
  { to: "/editions", label: "Editions" },
  { to: "/explore", label: "Registry" },
  { to: "/transparency", label: "Transparency" },
  { to: "/impact", label: "Impact" },
  { to: "/verify", label: "Verify" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen relative" style={{ background: "var(--hy-bg)" }}>
      <div className="hy-starfield" />
      <header className="sticky top-0 z-50 hy-glass" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <Moon className="w-6 h-6" style={{ color: "var(--hy-gold)" }} strokeWidth={1.4} />
            <div className="leading-none">
              <div className="font-display text-lg tracking-[0.35em] text-white">HYLUNER</div>
              <div className="hy-label mt-1 text-[0.55rem]">Celestial Registry</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${isActive ? "text-amber-300" : "text-slate-300 hover:text-amber-200"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin" className="text-sm text-amber-300 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" /> Admin
                  </Link>
                )}
                <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm text-slate-200 flex items-center gap-2 hover:text-amber-200">
                  <LayoutDashboard className="w-4 h-4" /> {user.name.split(" ")[0]}
                </Link>
                <button onClick={doLogout} data-testid="logout-btn" className="text-slate-400 hover:text-amber-300">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login" className="text-sm text-slate-200 hover:text-amber-200">Sign in</Link>
            )}
            <Link to="/create" data-testid="nav-create-cta" className="hy-btn px-6 py-3 text-xs uppercase">
              Register a Dedication
            </Link>
          </div>

          <button className="lg:hidden text-amber-300" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-amber-500/15 px-5 py-6 space-y-4" data-testid="mobile-menu">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-slate-200">{l.label}</Link>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="block text-slate-200">Dashboard</Link>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="block text-amber-300">Admin</Link>}
                <button onClick={doLogout} className="block text-slate-400">Sign out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="block text-slate-200">Sign in</Link>
            )}
            <Link to="/create" onClick={() => setOpen(false)} className="hy-btn inline-block px-6 py-3 text-xs uppercase">Register a Dedication</Link>
          </div>
        )}
      </header>

      <main className="relative z-10">{children}</main>

      <footer className="relative z-10 border-t border-amber-500/15 mt-28" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid gap-12 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="font-display text-xl tracking-[0.3em] text-white">HYLUNER</div>
            <p className="font-serif-accent italic text-slate-400 mt-3">Moon • Stars • Memories • Verified</p>
            <p className="text-xs text-slate-500 mt-6 max-w-md leading-relaxed">
              HYLUNER operates a private commemorative celestial registry. Registrations are symbolic
              dedications. They do not grant legal ownership, government-recognised title or property
              rights over the Moon, any star, planet or other celestial body. HYLUNER is not an
              astronomical naming authority.
            </p>
          </div>
          <div>
            <div className="hy-label mb-4">Registry</div>
            <div className="space-y-2 text-sm text-slate-400">
              <Link to="/editions" className="block hover:text-amber-200">Editions & Pricing</Link>
              <Link to="/explore" className="block hover:text-amber-200">Public Registry</Link>
              <Link to="/verify" className="block hover:text-amber-200">Verify a Certificate</Link>
            </div>
          </div>
          <div>
            <div className="hy-label mb-4">Trust</div>
            <div className="space-y-2 text-sm text-slate-400">
              <Link to="/transparency" className="block hover:text-amber-200">Transparency Statement</Link>
              <Link to="/impact" className="block hover:text-amber-200">HYLUNER Impact</Link>
              <Link to="/transparency#what-you-get" className="block hover:text-amber-200">What You Receive</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-amber-500/10 py-6 text-center text-xs text-slate-600">
          © {new Date().getFullYear()} HYLUNER — The Transparent Celestial Registry
        </div>
      </footer>
    </div>
  );
};
